import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./_utils";

const spinnerVariants = cva("animate-spin text-current", {
  variants: {
    size: {
      sm: "h-3 w-3",
      md: "h-4 w-4",
    },
  },
  defaultVariants: { size: "md" },
});

type SpinnerProps = React.SVGAttributes<SVGSVGElement> &
  VariantProps<typeof spinnerVariants>;

/**
 * Indeterminate loading spinner. Renders with `role="status"` and
 * `aria-label="Loading"` by default so screen readers announce the loading
 * state when the spinner is the only indicator. Pass `aria-label` to
 * customise the announcement, or `aria-hidden="true"` to suppress it when
 * the spinner is decorative (e.g. inside a Button that already has a label).
 *
 * @example
 * <Spinner />
 * <Spinner size="sm" />
 * <Spinner aria-label="Saving changes" />
 */
export function Spinner({ className, size, ...props }: SpinnerProps) {
  return (
    <svg
      role="status"
      aria-label="Loading"
      className={cn(spinnerVariants({ size }), className)}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeOpacity="0.25"
        strokeWidth="3"
      />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}
